import { create } from "zustand";
import type { AutoSwapMode } from "@/hooks/useAutoSwapSettings";
import { V2_ENDPOINT } from "../consts";
import {
  trackWorkflowArchived,
  trackWorkflowCreated,
  trackWorkflowPublished,
  trackWorkflowRun,
} from "../lib/analytics";
import {
  publishWidget as publishWidgetApi,
  unpublishWidget as unpublishWidgetApi,
  type PublishWidgetRequest,
} from "../lib/api/publicWidget";
import { fetchWorkflowById } from "../app/(v2)/workflows/_hooks/workflowsApi";
import { ApiError, authenticatedFetch } from "../lib/authenticatedFetch";
import { getQueryClient } from "../lib/queryClient";
import { emitWorkflowCatalogSync } from "../lib/workflow/catalogSync";
import type { PaginatedData } from "../types/pagination";
import type { WorkflowComment, WorkflowStickyNote } from "../types/workflow-comments";

// --- TanStack Query cache invalidation helpers --------------------------
// These keep the server-paginated /workflows page in sync after mutations
// performed through this Zustand store. We only invalidate on the browser —
// on the server there's no user-scoped cache to flush.
//
// Keys mirror `app/(v2)/workflows/_hooks/workflowsQueryKeys.ts`. They're
// duplicated here (rather than imported) to keep the store free of React/
// routing dependencies.
function invalidateWorkflowListCache(opts: { workflowId?: string; includeOwnerCounts?: boolean } = {}): void {
  if (typeof window === "undefined") return;
  const qc = getQueryClient();
  qc.invalidateQueries({ queryKey: ["workflows", "list"] });
  qc.invalidateQueries({ queryKey: ["workflows", "counts"] });
  // Detail cache: pause/resume/publish/archive/update/visibility mutate
  // a single workflow. Consumers of useWorkflow(id) would otherwise
  // render stale data for up to DETAIL_STALE_MS (30s). Prefer scoped
  // invalidation when the caller knows the ID — broad fallback causes
  // a refetch storm of every cached detail entry in the session.
  if (opts.workflowId) {
    qc.invalidateQueries({ queryKey: ["workflows", "detail", opts.workflowId] });
  } else {
    qc.invalidateQueries({ queryKey: ["workflows", "detail"] });
  }
  // Any mutation that rewrites a workflow's definition can add or remove
  // a trigger-workflow node, which flips the incoming-links set of OTHER
  // workflows. We don't know which target IDs were affected, so flush
  // the whole subtree — entries refetch lazily on next panel open.
  qc.invalidateQueries({ queryKey: ["workflows", "incoming-links"] });
  if (opts.includeOwnerCounts) {
    qc.invalidateQueries({ queryKey: ["workflows", "owner-counts"] });
  }
}

// Re-export for consumers that import from this file
export type { PublishWidgetRequest } from "../lib/api/publicWidget";

// Workflow type definitions based on API response

// X402 payment configuration types
export interface X402DiscoveryMetadata {
  name?: string;
  description?: string;
  category?: string;
  tags?: string[];
  documentation?: string;
  logo?: string;
  provider?: string;
}

export interface X402DiscoverySchema {
  example?: unknown;
  schema?: Record<string, unknown>;
}

export interface X402Config {
  price: string;
  token: string;
  chainId: number;
  recipient: string;
  discoverable?: boolean;
  discoveryMetadata?: X402DiscoveryMetadata;
  discoveryInput?: X402DiscoverySchema;
  discoveryOutput?: X402DiscoverySchema;
}

// Public widget configuration for sharing workflows
export interface PublicWidgetConfig {
  enabled: boolean;
  title: string;
  description?: string;
  buttonLabel?: string;
  recipientAddress: string;
  chainId: number;
  tokenAddress?: string;
  minAmount?: string;
  maxAmount?: string;
}

// UI position of a node on the canvas
export interface NodePosition {
  x: number;
  y: number;
}

// UI-only data that doesn't affect execution
export interface WorkflowUIMetadata {
  nodePositions?: Record<string, NodePosition>;
  stickyNotes?: WorkflowStickyNote[];
  comments?: WorkflowComment[];
}

export interface WorkflowNodeConnector {
  id?: string; // e.g., Turnkey subOrgId - optional for templates
  type: string; // e.g., "wallet"
}

export interface WorkflowNodePosition {
  x: number;
  y: number;
}

export interface WorkflowNode {
  type: string;
  payload: Record<string, unknown>;
  children: string[]; // Always an array (may be empty)
  loopBody?: string[]; // Loop body nodes (only for for-each nodes)
  branch?: "then" | "else" | "resumed" | "timed_out"; // For conditional branches (if-then-else, wait-resumed-timed_out)
  connector?: WorkflowNodeConnector;
  description?: string; // AI-generated description of what this node does
  titleOverride?: string; // AI-generated short title (2-6 words), only when more descriptive than default
  position?: WorkflowNodePosition; // Node position for canvas layout persistence
}

export interface WorkflowDefinition {
  nodes: Record<string, WorkflowNode>;
  inputSchema?: WorkflowInput[];
}

export interface WorkflowInput {
  key: string;
  type: "string" | "number" | "boolean" | "object" | "array";
  description?: string;
  required: boolean;
}

// Summary node shape returned by the list endpoint's definitionSummary
export interface DefinitionSummaryNode {
  type: string;
  children?: string[];
  loopBody?: string[];
  position?: { x: number; y: number };
  branch?: "then" | "else" | "resumed" | "timed_out";
}

export interface DefinitionSummary {
  nodes: Record<string, DefinitionSummaryNode>;
}

export interface Workflow {
  id: string;
  organizationId?: string;
  createdBy?: string;
  ownerUserId?: string;
  name: string;
  description: string;
  baseWorkflowId?: string;
  version: number;
  status: "draft" | "active" | "paused" | "archived";
  pausedReason?: "cu_exhausted" | "disabled_action" | "consecutive_failures" | null;
  type: string;
  maxRuns: number;
  remainRuns: number;
  cooldownMs: number;
  lastTriggeredAt?: string;
  createdAt: string;
  updatedAt?: string;

  // Draft/Live coexistence fields
  hasDraft?: boolean;
  hasLiveVersion?: boolean;

  // Pre-extracted fields from slim list response
  triggerType?: string;
  triggerName?: string;
  chainIds?: number[];
  connectors?: Array<{ type: string }>;
  nodeCount?: number;
  publicWidgetEnabled?: boolean;
  definitionSummary?: DefinitionSummary;
  /** Count of definition.inputSchema entries. Mirrors list_metadata.inputCount. */
  inputCount?: number;
  /** IDs of workflows this workflow triggers via `trigger-workflow` nodes.
   * Mirrors list_metadata.linkedWorkflowIds — lets useLinkedWorkflows build
   * the forward/reverse link graph from slim list items. */
  linkedWorkflowIds?: string[];

  // Full fields -- present on detail endpoint, absent in slim list response
  definition?: WorkflowDefinition;
  triggerSecret?: string;
  x402Config?: X402Config;
  publicWidgetConfig?: PublicWidgetConfig;
  visibility?: WorkflowVisibility;
  fundAutoSwapConfig?: {
    enabled: boolean;
    mode: AutoSwapMode;
    maxSwapAmountUsd: number;
    sources: Array<{
      id: string;
      walletId: string;
      tokenAddress: string;
      chainId: number;
      tokenDecimals: number;
      slippageBps: number;
      priority: number;
    }>;
  };
  uiMetadata?: WorkflowUIMetadata;
  aiName?: string;
  aiDescription?: string;
  conversationId?: string;
}

export type WorkflowVisibility = "private" | "org" | "public" | "public_view" | "public_execute";

export interface WorkflowsApiResponse {
  code: number;
  message: string;
  data: PaginatedData<Workflow>;
  requestId: string;
}

export interface CreateWorkflowRequest {
  name: string;
  description: string;
  maxRuns?: number;
  cooldownMs?: number;
  definition: WorkflowDefinition;
  templateId?: string;
  uiMetadata?: WorkflowUIMetadata;
  sessionId?: string;
  conversationId?: string;
}

export interface RunWorkflowPayload {
  /** Client-generated run ID for subscribe-first SSE pattern */
  runId?: string;
  /** Assistant chat message that launched the run, for durable history linkage */
  chatMessageId?: string;
  /** When true, EVM transactions are simulated rather than executed. Used to derive analytics mode (test vs live). */
  simulate?: boolean;
  payload?: Record<string, unknown>;
}

export interface RunWorkflowResponse {
  runId: string;
}

export interface WorkflowVersion {
  id: string;
  version: number;
  status: "draft" | "active" | "paused" | "archived";
  name: string;
  description: string;
  createdAt: string;
}

export interface RollbackWorkflowPayload {
  targetVersion: number;
  expectedCurrentVersion?: number;
}

interface WorkflowsStore {
  reset: () => void;
  createWorkflow: (request: CreateWorkflowRequest) => Promise<Workflow>;
  duplicateWorkflow: (workflowId: string) => Promise<Workflow>;
  pauseWorkflow: (workflowId: string) => Promise<void>;
  resumeWorkflow: (workflowId: string) => Promise<void>;
  archiveWorkflow: (workflowId: string) => Promise<void>;
  updateWorkflow: (workflowId: string, payload: UpdateWorkflowPayload) => Promise<void>;
  publishWorkflow: (workflowId: string) => Promise<Workflow>;
  runWorkflow: (workflowId: string, payload?: RunWorkflowPayload) => Promise<RunWorkflowResponse>;
  rollbackWorkflow: (workflowId: string, payload: RollbackWorkflowPayload) => Promise<Workflow>;
  // Draft/Live version methods
  fetchWorkflowLive: (workflowId: string) => Promise<Workflow>;
  discardDraft: (workflowId: string) => Promise<void>;
  // Public widget methods
  publishWidgetConfig: (workflowId: string, config: PublishWidgetRequest) => Promise<PublicWidgetConfig>;
  unpublishWidgetConfig: (workflowId: string) => Promise<void>;
  // Visibility
  updateVisibility: (workflowId: string, visibility: WorkflowVisibility) => Promise<void>;
  transferOwnership: (workflowId: string, ownerUserId: string) => Promise<void>;
}

export interface UpdateWorkflowPayload {
  name?: string;
  description?: string;
  maxRuns?: number;
  cooldownMs?: number;
  definition?: WorkflowDefinition;
  uiMetadata?: WorkflowUIMetadata;
  expectedCurrentVersion?: number;
}

// Fetch a single workflow through the TanStack cache so duplicateWorkflow
// shares work with any open detail view. Was previously a direct
// authenticatedFetch — redundant round-trip whenever the source was already
// cached (e.g. the user just opened it in the editor).
async function fetchWorkflowByIdInternal(workflowId: string): Promise<Workflow> {
  if (typeof window === "undefined") {
    // SSR / test: no query client available — hit the API directly.
    return fetchWorkflowById(workflowId);
  }
  return getQueryClient().fetchQuery({
    queryKey: ["workflows", "detail", workflowId],
    queryFn: () => fetchWorkflowById(workflowId),
  });
}

export const useWorkflowsStore = create<WorkflowsStore>(() => ({
  // `reset()` is retained because `useOrganizations` calls it on org switch.
  // The store no longer holds any server-cached state (TanStack Query owns
  // the workflow list/detail cache now), so this is effectively a no-op —
  // TanStack invalidation happens alongside the call in the consumer.
  reset: () => {
    // intentionally empty — no local state left to clear
  },

  createWorkflow: async (request: CreateWorkflowRequest): Promise<Workflow> => {
    try {
      const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/workflows`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to create workflow: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(`API error: ${data.message}`);
      }

      const newWorkflow: Workflow = data.data;

      // Seed the detail cache with the full just-created workflow so
      // toast name lookups (richToasts.getWorkflowName) and immediate
      // navigations to the new detail view don't round-trip. aqt#2.
      if (typeof window !== "undefined") {
        getQueryClient().setQueryData(["workflows", "detail", newWorkflow.id], newWorkflow);
      }
      invalidateWorkflowListCache({ includeOwnerCounts: true });

      console.log(`Workflow "${newWorkflow.name}" created successfully with ID: ${newWorkflow.id}`);

      // Track workflow creation
      trackWorkflowCreated({
        id: newWorkflow.id,
        type: newWorkflow.type,
        nodeCount: Object.keys(newWorkflow.definition?.nodes || {}).length,
      });

      emitWorkflowCatalogSync();

      return newWorkflow;
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to create workflow");
      console.error("Failed to create workflow", error);
      throw error;
    }
  },

  duplicateWorkflow: async (workflowId: string): Promise<Workflow> => {
    try {
      console.log(`🔄 [duplicateWorkflow] Duplicating workflow: ${workflowId}`);

      // Fetch full workflow details to get the definition (needed to create the copy).
      const originalWorkflow = await fetchWorkflowByIdInternal(workflowId);
      if (!originalWorkflow.definition) {
        throw new Error(`Workflow ${workflowId} definition not available`);
      }

      // Create a new workflow request based on the original
      const duplicateRequest: CreateWorkflowRequest = {
        name: `Copy of ${originalWorkflow.name}`,
        description: originalWorkflow.description,
        maxRuns: originalWorkflow.maxRuns,
        cooldownMs: originalWorkflow.cooldownMs,
        definition: originalWorkflow.definition,
        uiMetadata: originalWorkflow.uiMetadata,
      };

      // Use the existing createWorkflow method
      const newWorkflow = await useWorkflowsStore.getState().createWorkflow(duplicateRequest);

      console.log(`✅ [duplicateWorkflow] Workflow duplicated successfully with ID: ${newWorkflow.id}`);

      return newWorkflow;
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to duplicate workflow");
      console.error("❌ [duplicateWorkflow] Failed to duplicate workflow", error);
      throw error;
    }
  },

  pauseWorkflow: async (workflowId: string): Promise<void> => {
    try {
      const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/workflows/${workflowId}/pause`, {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error(`Failed to pause workflow: ${response.statusText}`);
      }

      invalidateWorkflowListCache({ workflowId });

      console.log(`Workflow ${workflowId} paused successfully`);
      emitWorkflowCatalogSync();
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to pause workflow");
      console.error("Failed to pause workflow", error);
      throw error;
    }
  },

  resumeWorkflow: async (workflowId: string): Promise<void> => {
    try {
      const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/workflows/${workflowId}/resume`, {
        method: "POST",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        const msg = errorData.message || `Failed to resume workflow: ${response.statusText}`;
        throw errorData.code
          ? new ApiError(msg, errorData.code, Array.isArray(errorData.details) ? errorData.details : [])
          : new Error(msg);
      }

      invalidateWorkflowListCache({ workflowId });

      console.log(`Workflow ${workflowId} resumed successfully`);
      emitWorkflowCatalogSync();
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to resume workflow");
      console.error("Failed to resume workflow", error);
      throw error;
    }
  },

  archiveWorkflow: async (workflowId: string): Promise<void> => {
    try {
      const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/workflows/${workflowId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`Failed to archive workflow: ${response.statusText}`);
      }

      invalidateWorkflowListCache({ workflowId, includeOwnerCounts: true });

      console.log(`Workflow ${workflowId} archived successfully`);

      // Track workflow archived
      trackWorkflowArchived(workflowId);
      emitWorkflowCatalogSync();
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to archive workflow");
      console.error("Failed to archive workflow", error);
      throw error;
    }
  },

  updateWorkflow: async (workflowId: string, payload: UpdateWorkflowPayload): Promise<void> => {
    try {
      console.log("🔄 [updateWorkflow] Updating workflow:", workflowId);
      console.log("🔄 [updateWorkflow] Payload:", payload);

      const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/workflows/${workflowId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        // Handle optimistic locking conflict (409)
        if (response.status === 409) {
          throw new Error(errorData.message || "Workflow was modified by another user, please refresh and try again");
        }
        throw new Error(errorData.message || `Failed to update workflow: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(`API error: ${data.message}`);
      }

      const updatedWorkflow: Workflow = data.data;
      invalidateWorkflowListCache({ workflowId });

      console.log(
        `✅ [updateWorkflow] Workflow ${workflowId} updated successfully, version: ${updatedWorkflow?.version}, status: ${updatedWorkflow?.status}`,
      );
      emitWorkflowCatalogSync();
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to update workflow");
      console.error("❌ [updateWorkflow] Failed to update workflow", error);
      throw error;
    }
  },

  publishWorkflow: async (workflowId: string): Promise<Workflow> => {
    try {
      console.log("🚀 [publishWorkflow] Publishing workflow:", workflowId);

      const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/workflows/${workflowId}/publish`, {
        method: "POST",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        const msg = errorData.message || `Failed to publish workflow: ${response.statusText}`;
        throw errorData.code
          ? new ApiError(msg, errorData.code, Array.isArray(errorData.details) ? errorData.details : [])
          : new Error(msg);
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(`API error: ${data.message}`);
      }

      const publishedWorkflow: Workflow = data.data;

      invalidateWorkflowListCache({ workflowId });

      console.log(`✅ [publishWorkflow] Workflow ${workflowId} published successfully`);

      // Track workflow published
      trackWorkflowPublished(workflowId);
      emitWorkflowCatalogSync();

      return publishedWorkflow;
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to publish workflow");
      console.error("❌ [publishWorkflow] Failed to publish workflow", error);
      throw error;
    }
  },

  runWorkflow: async (workflowId: string, runPayload?: RunWorkflowPayload): Promise<RunWorkflowResponse> => {
    try {
      console.log("▶️ [runWorkflow] Running workflow:", workflowId);
      console.log("▶️ [runWorkflow] Payload:", runPayload);

      const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/workflows/${workflowId}/run`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(runPayload || {}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Failed to run workflow: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(`API error: ${data.message}`);
      }

      const runId = data.data?.runId;
      if (!runId) {
        throw new Error("Run started but no runId returned from API");
      }
      console.log(`✅ [runWorkflow] Workflow ${workflowId} run started with runId:`, runId);

      // Track workflow run
      trackWorkflowRun(workflowId, runId, runPayload?.simulate ? "test" : "live");

      return { runId };
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to run workflow");
      console.error("❌ [runWorkflow] Failed to run workflow", error);
      throw error;
    }
  },

  rollbackWorkflow: async (workflowId: string, payload: RollbackWorkflowPayload): Promise<Workflow> => {
    try {
      console.log("⏪ [rollbackWorkflow] Rolling back workflow:", workflowId, "to version:", payload.targetVersion);

      const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/workflows/${workflowId}/rollback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        // Handle optimistic locking conflict (409)
        if (response.status === 409) {
          throw new Error(errorData.message || "Workflow was modified by another user, please refresh and try again");
        }
        throw new Error(errorData.message || `Failed to rollback workflow: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(`API error: ${data.message}`);
      }

      const rolledBackWorkflow: Workflow = data.data;

      invalidateWorkflowListCache({ workflowId });

      console.log(`✅ [rollbackWorkflow] Workflow ${workflowId} rolled back to version ${payload.targetVersion}`);
      emitWorkflowCatalogSync();
      return rolledBackWorkflow;
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to rollback workflow");
      console.error("❌ [rollbackWorkflow] Failed to rollback workflow", error);
      throw error;
    }
  },

  fetchWorkflowLive: async (workflowId: string): Promise<Workflow> => {
    try {
      console.log("🟢 [fetchWorkflowLive] Fetching live version for workflow:", workflowId);

      const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/workflows/${workflowId}/live`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Failed to fetch live workflow: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(`API error: ${data.message}`);
      }

      if (!data.data) {
        throw new Error("Live workflow version not found");
      }

      console.log("✅ [fetchWorkflowLive] Live version fetched for workflow:", workflowId);
      return data.data;
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to fetch live workflow");
      console.error("❌ [fetchWorkflowLive] Failed to fetch live version", error);
      throw error;
    }
  },

  discardDraft: async (workflowId: string): Promise<void> => {
    try {
      console.log("🗑️ [discardDraft] Discarding draft for workflow:", workflowId);

      const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/workflows/${workflowId}/draft`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Failed to discard draft: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(`API error: ${data.message}`);
      }

      invalidateWorkflowListCache({ workflowId });

      console.log("✅ [discardDraft] Draft discarded for workflow:", workflowId);
      emitWorkflowCatalogSync();
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to discard draft");
      console.error("❌ [discardDraft] Failed to discard draft", error);
      throw error;
    }
  },

  publishWidgetConfig: async (workflowId: string, config: PublishWidgetRequest): Promise<PublicWidgetConfig> => {
    try {
      console.log("🔗 [publishWidgetConfig] Publishing widget for workflow:", workflowId);

      const widgetConfig = await publishWidgetApi(workflowId, config);

      invalidateWorkflowListCache({ workflowId });

      console.log(`✅ [publishWidgetConfig] Widget published for workflow ${workflowId}`);
      emitWorkflowCatalogSync();
      return widgetConfig;
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to publish widget config");
      console.error("❌ [publishWidgetConfig] Failed to publish widget", error);
      throw error;
    }
  },

  unpublishWidgetConfig: async (workflowId: string): Promise<void> => {
    try {
      console.log("🔗 [unpublishWidgetConfig] Unpublishing widget for workflow:", workflowId);

      await unpublishWidgetApi(workflowId);

      invalidateWorkflowListCache({ workflowId });

      console.log(`✅ [unpublishWidgetConfig] Widget unpublished for workflow ${workflowId}`);
      emitWorkflowCatalogSync();
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to unpublish widget config");
      console.error("❌ [unpublishWidgetConfig] Failed to unpublish widget", error);
      throw error;
    }
  },

  updateVisibility: async (workflowId: string, visibility: WorkflowVisibility): Promise<void> => {
    const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/workflows/${workflowId}/visibility`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visibility }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.message || "Failed to update visibility");
    }

    // Patch the detail cache so useWorkflow(id) consumers see the new value
    // synchronously — the invalidation below kicks off a background refetch
    // for confirmation, but waiting on it leaves the UI showing the previous
    // visibility for a perceptible moment after save.
    if (typeof window !== "undefined") {
      getQueryClient().setQueryData(["workflows", "detail", workflowId], (old: Workflow | undefined) =>
        old ? { ...old, visibility } : old,
      );
    }
    invalidateWorkflowListCache({ workflowId, includeOwnerCounts: true });
  },

  transferOwnership: async (workflowId: string, ownerUserId: string): Promise<void> => {
    const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/workflows/${workflowId}/owner`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ownerUserId }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.message || "Failed to transfer workflow ownership");
    }

    // Same optimistic patch as updateVisibility — keep the detail cache in
    // sync with the mutation we just confirmed on the server.
    if (typeof window !== "undefined") {
      getQueryClient().setQueryData(["workflows", "detail", workflowId], (old: Workflow | undefined) =>
        old ? { ...old, ownerUserId } : old,
      );
    }
    invalidateWorkflowListCache({ workflowId, includeOwnerCounts: true });
  },
}));
